import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { hueVars } from "../data/artists";
import ArtworkMedia from "./ArtworkMedia";

const EASE = [0.22, 1, 0.36, 1];

/* a view's `follow` media plays inside its section of the auto-cycle, without its own toggle;
   the piece's holding image leads the cycle when it differs from the first view's still */
const cycleMedia = (piece) => {
  if (!piece.views) return null;
  const list = piece.views.flatMap((v) => [v, ...(v.follow || [])]);
  if (piece.image && list[0] && list[0].image !== piece.image) list.unshift({ image: piece.image });
  return list;
};

export default function PeriodTimeline({ artist, onViewPiece }) {
  const [open, setOpen] = useState(null);
  const [viewIdx, setViewIdx] = useState(0);

  /* panels with multiple views auto-play through them, one every few seconds */
  useEffect(() => {
    setViewIdx(0);
    if (open === null) return;
    const list = cycleMedia(artist.periods[open].piece);
    if (!list || list.length < 2) return;
    const id = setInterval(() => setViewIdx((v) => (v + 1) % list.length), 4500);
    return () => clearInterval(id);
  }, [open, artist]);

  return (
    <div className="timeline" data-testid="period-timeline">
      {artist.periods.map((p, i) => {
        const isOpen = open === i;
        return (
          <div key={p.era} className="tl-item">
            <motion.button
              className="tl-row"
              data-testid={`period-row-${i + 1}`}
              onClick={() => setOpen(isOpen ? null : i)}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.8, ease: EASE }}
              style={hueVars(p.hue)}
              aria-expanded={isOpen}
            >
              <span className="t-no">{String(i + 1).padStart(2, "0")}</span>
              <span className="t-era display">{p.era}</span>
              <span className="t-years">{p.years}</span>
              <span className="t-plus">{isOpen ? "−" : "+"}</span>
            </motion.button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  className={`tl-panel era-${p.slug}`}
                  data-testid={`period-panel-${i + 1}`}
                  style={hueVars(p.hue)}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.55, ease: EASE }}
                >
                  <div className="tl-panel-inner">
                    <div className="t-history">
                      <div className="t-era-big display">{p.era}</div>
                      <div className="t-years-big">{p.years}</div>
                      {p.info.split("\n\n").map((para, k) => (
                        <p key={k}>{para}</p>
                      ))}
                    </div>
                    <div className="t-piece">
                      <div className={`piece-panel tl-frame${p.piece.landscape ? " wide" : ""}`}>
                        {(() => {
                          const list = cycleMedia(p.piece);
                          const media = list ? list[viewIdx % list.length] : p.piece;
                          return media && (media.image || media.video) ? (
                            <ArtworkMedia key={`${i}-${viewIdx}`} piece={media} alt={`${p.piece.title} — ${p.era}`} />
                          ) : (
                            <>
                              <div className="surface texture" style={hueVars(p.hue)} />
                              <span className="asset-tag">Awaiting artwork — 4:5</span>
                            </>
                          );
                        })()}
                      </div>
                      <button
                        className="view-piece-btn"
                        data-testid={`view-period-piece-${i + 1}`}
                        onClick={() => onViewPiece({ name: artist.name, hue: p.hue, piece: p.piece })}
                      >
                        View the Piece
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}
